import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAdminPath, isAdminPathConfigured } from '@/config/admin';
import { clearPersistedSession } from '@/constants/storageKeys';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints where a 401 means "bad credentials/code", not "expired
// session"; they must never trigger a token refresh or a forced redirect.
// Public signing endpoints are token-authenticated for external signers who
// have no session at all, so they must never touch the refresh/login flow.
const NO_REFRESH_PATHS = ['/auth/login', '/auth/mfa/verify', '/auth/refresh', '/auth/register', '/public/'];

// Single in-flight refresh shared by every request that hits a 401 at the
// same time. Refresh tokens are rotated, so concurrent refreshes with the
// same token make all but the first fail and sign the user out.
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token'));
    }
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
      .then((response) => {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return accessToken as string;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const redirectToLogin = () => {
  // Redirect to the appropriate login page based on current path.
  // Only treat the current URL as an admin route if a strong admin
  // path is actually configured; otherwise always fall back to the
  // public login (prevents leaking admin paths).
  if (isAdminPathConfigured()) {
    const adminPath = getAdminPath();
    if (window.location.pathname.startsWith(`/${adminPath}`)) {
      window.location.href = `/${adminPath}/login`;
      return;
    }
  }
  window.location.href = '/login';
};

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = NO_REFRESH_PATHS.some((p) => requestUrl.includes(p));

    // Handle 401 - try to refresh token
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint &&
      localStorage.getItem('refreshToken')
    ) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Only an explicit rejection of the refresh token ends the session;
        // a network blip must not sign the user out.
        const status = (refreshError as AxiosError).response?.status;
        if (status === 401 || status === 403 || status === 400) {
          clearPersistedSession();
          redirectToLogin();
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
